import React, { useState } from "react";
import { Form, Modal, Button } from "react-bootstrap";
import { round2, grossUp, MONEY_DECIMALS, MONEY_STEP } from "./feeTax";
import "./OutstandingGrid.css";

interface HeadItem {
  headId: number;
  headName: string;
  planAmount: number;
  paidAmount: number;
  /** Gross paid (base + attributed tax). Optional — falls back to paidAmount. */
  paidGross?: number;
  /** Tax slice attributed to this base head — shown as "(tax: N)" beside the gross. */
  paidTaxAmount?: number;
  headTaxAmount: number;
  headTaxPerc: number;
  discountAmount: number;
  outstanding: number;
}

interface InstallmentItem {
  installmentId: number;
  installmentMappingId: number | null;
  installmentName?: string | null;
  dueDate: string | null;
  heads: HeadItem[];
}

interface OutstandingGridProps {
  installments: InstallmentItem[];
  amounts: Record<string, number>; // key = "installmentId-headId"
  onAmountChange: (installmentId: number, headId: number, amount: number) => void;
  /**
   * Optional sink for Quick-fill surplus. When the user runs Quick-fill with
   * an amount greater than the total outstanding, the surplus is reported
   * through this callback so the parent can route it into an Advance / Excess
   * field. When omitted, the grid falls back to a modal alert (preserves the
   * old behaviour for callers without an excess sink, e.g. CollectPaymentModal).
   */
  onExcessAmountChange?: (surplus: number) => void;
  readOnly?: boolean;
}

// Precision from the single knob MONEY_DECIMALS (config/money): 2 decimals
// minimum (money still reads as money), up to MONEY_DECIMALS. At the default 2
// this is the classic 2dp display; at 8 it shows the finer computed value.
const fmtMoney = (n: number) =>
  n.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: MONEY_DECIMALS,
  });

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const parseDueDate = (s: string | null): Date | null => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
};

const fmtDueDate = (d: Date | null): string => {
  if (!d) return "—";
  return `${String(d.getDate()).padStart(2, "0")} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const startOfDay = (d: Date) =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

type Status = "overdue" | "due-today" | "upcoming" | "unknown";

const statusOf = (due: Date | null): Status => {
  if (!due) return "unknown";
  const today = startOfDay(new Date());
  const dueT = startOfDay(due);
  if (dueT < today) return "overdue";
  if (dueT === today) return "due-today";
  return "upcoming";
};

const statusLabel: Record<Status, string> = {
  "overdue": "Overdue",
  "due-today": "Due Today",
  "upcoming": "Upcoming",
  "unknown": "No due date",
};

const OutstandingGrid: React.FC<OutstandingGridProps> = ({
  installments,
  amounts,
  onAmountChange,
  onExcessAmountChange,
  readOnly = false,
}) => {
  const [adjustAmount, setAdjustAmount] = useState(0);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // Filter to installments that have at least one payable head.
  const payableInstallments = installments
    .map((inst) => ({
      ...inst,
      payableHeads: inst.heads.filter((h) => h.outstanding > 0),
    }))
    .filter((inst) => inst.payableHeads.length > 0);

  const totalOutstandingWithTax = round2(
    payableInstallments.reduce(
      (sum, inst) =>
        sum +
        inst.payableHeads.reduce(
          (s, h) => s + grossUp(h.outstanding, h.headTaxPerc),
          0,
        ),
      0,
    ),
  );

  const totalEntered = Object.values(amounts).reduce((s, v) => s + (v || 0), 0);

  const setAdjustAmounts = () => {
    const allocatable = round2(Math.min(adjustAmount, totalOutstandingWithTax));
    const surplus = round2(adjustAmount - allocatable);

    // No sink for surplus → preserve the old modal-alert behaviour for callers
    // (e.g. CollectPaymentModal, AdjustCreditModal) that have no Advance field.
    if (surplus > 0 && !onExcessAmountChange) {
      setAlertMessage(
        `Entered amount (${fmtMoney(adjustAmount)}) is greater than the total outstanding (${fmtMoney(totalOutstandingWithTax)}). Use the Advance / Excess field below to record additional amount as credit.`,
      );
      setAdjustAmount(0);
      return;
    }

    let remaining = allocatable;
    payableInstallments.forEach((inst) => {
      inst.payableHeads.forEach((head) => {
        const grossDue = grossUp(head.outstanding, head.headTaxPerc);
        const take = remaining > 0 ? round2(Math.min(remaining, grossDue)) : 0;
        onAmountChange(inst.installmentId, head.headId, take);
        remaining = round2(remaining - take);
      });
    });

    // Replace (not accumulate) — running Quick-fill is an explicit reset, so
    // clear any previously auto-routed excess back to zero when the new total
    // fits within outstanding.
    if (onExcessAmountChange) {
      onExcessAmountChange(surplus);
    }

    setAdjustAmount(0);
  };

  if (payableInstallments.length === 0) {
    return (
      <div className="outstanding-grid">
        <div className="og-empty">
          <i className="fa fa-check-circle" /> No outstanding fees for this enrollment.
        </div>
      </div>
    );
  }

  return (
    <div className="outstanding-grid">
      {!readOnly && (
        <div className="og-quickfill">
          <div className="og-quickfill-text">
            <div className="og-quickfill-label">Quick-fill across installments</div>
            <div className="og-quickfill-hint">
              Distributes the amount in due-order, paying each head up to its outstanding (with tax).
            </div>
          </div>
          <Form.Control
            type="number"
            size="sm"
            min={0}
            step={MONEY_STEP}
            placeholder="0.00"
            className="og-quickfill-input"
            value={adjustAmount || ""}
            onChange={(e) =>
              setAdjustAmount(round2(Number(e.target.value) || 0))
            }
          />
          <button
            type="button"
            className="og-quickfill-btn"
            onClick={setAdjustAmounts}
            disabled={adjustAmount <= 0}
          >
            Auto-allocate
          </button>
        </div>
      )}

      {payableInstallments.map((inst, idx) => {
        const due = parseDueDate(inst.dueDate);
        const status = statusOf(due);

        const instOutstanding = round2(
          inst.payableHeads.reduce(
            (s, h) => s + grossUp(h.outstanding, h.headTaxPerc),
            0,
          ),
        );

        const instPaying = round2(
          inst.payableHeads.reduce((s, h) => {
            const k = `${inst.installmentId}-${h.headId}`;
            return s + (amounts[k] || 0);
          }, 0),
        );

        return (
          <div
            key={inst.installmentId}
            className={`og-installment-card og-status-bar-${status}`}
          >
            <div className="og-installment-header">
              <div className="og-installment-title">
                <div className="og-installment-label">
                  <span className="og-installment-index">
                    {inst.installmentName || `Installment ${idx + 1}`}
                  </span>
                  <span className={`og-status-pill og-status-${status}`}>
                    {statusLabel[status]}
                  </span>
                </div>
                <div className="og-installment-duedate">
                  <i className="fa fa-calendar" /> Due {fmtDueDate(due)}
                </div>
              </div>
              <div className="og-installment-amounts">
                <div className="og-amount-block">
                  <div className="og-amount-label">Outstanding</div>
                  <div className="og-amount-value">₹{fmtMoney(instOutstanding)}</div>
                </div>
                <div className="og-amount-block og-amount-paying">
                  <div className="og-amount-label">Paying</div>
                  <div className="og-amount-value">₹{fmtMoney(instPaying)}</div>
                </div>
              </div>
            </div>

            <div className="og-heads-wrap">
              <table className="og-heads-table">
                <thead>
                  <tr>
                    <th className="og-th-head">Head</th>
                    <th className="og-th-num">Plan</th>
                    <th className="og-th-num">Paid</th>
                    <th className="og-th-num">Discount</th>
                    <th className="og-th-num">Tax</th>
                    <th className="og-th-num">Outstanding</th>
                    <th className="og-th-amt">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {inst.payableHeads.map((head) => {
                    const key = `${inst.installmentId}-${head.headId}`;
                    const grossDue = grossUp(
                      head.outstanding,
                      head.headTaxPerc,
                    );
                    return (
                      <tr key={key}>
                        <td className="og-td-head">{head.headName}</td>
                        <td className="og-td-num">{fmtMoney(head.planAmount)}</td>
                        <td className="og-td-num og-td-paid">
                          <div className="og-td-paid-gross">
                            ₹{fmtMoney(head.paidGross ?? head.paidAmount)}
                          </div>
                          {(head.paidTaxAmount ?? 0) > 0 && (
                            <span className="og-td-paid-tax-chip">
                              <i className="fa fa-percent" aria-hidden="true" />
                              incl. tax ₹{fmtMoney(head.paidTaxAmount!)}
                            </span>
                          )}
                        </td>
                        <td className="og-td-num">{fmtMoney(head.discountAmount)}</td>
                        <td className="og-td-num">{fmtMoney(head.headTaxAmount)}</td>
                        <td className="og-td-num og-td-outstanding">
                          ₹{fmtMoney(grossDue)}
                        </td>
                        <td className="og-td-amt">
                          {readOnly ? (
                            <span className="og-readonly-amt">
                              {fmtMoney(amounts[key] || 0)}
                            </span>
                          ) : (
                            <Form.Control
                              type="number"
                              size="sm"
                              className="og-amt-input"
                              min={0}
                              max={grossDue}
                              step={MONEY_STEP}
                              placeholder="0.00"
                              value={amounts[key] || ""}
                              onChange={(e) =>
                                onAmountChange(
                                  inst.installmentId,
                                  head.headId,
                                  round2(Number(e.target.value) || 0),
                                )
                              }
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      <div className="og-grand-total">
        <span>Total entered across all installments</span>
        <span className="og-grand-total-value">₹{fmtMoney(totalEntered)}</span>
      </div>

      <Modal
        show={alertMessage !== null}
        onHide={() => setAlertMessage(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Cannot auto-allocate</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ fontSize: 13 }}>{alertMessage}</Modal.Body>
        <Modal.Footer>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAlertMessage(null)}
          >
            OK
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OutstandingGrid;
